import React from 'react';
import { formatDateTime } from '../utils/dateFormatter';
import '../styles/BookingDetails.css';

/**
 * Component to display Channex-specific booking information
 */
const BookingChannexInfo = ({ booking, hideInternalDetails }) => {
  // Don't show if no Channex data
  if (!booking.channexData?.bookingId) {
    return null;
  }

  // Was a local en-GB formatter rendering in the browser's timezone — the change log
  // beside it already showed Riyadh, so the same event read as two different times.
  const formatDate = formatDateTime;

  const getBookingStateColor = (state) => {
    const colors = {
      received: '#6c757d',
      validating: '#ffc107',
      confirmed: '#28a745',
      conflicted: '#dc3545',
      syncing: '#17a2b8',
      completed: '#20c997',
      cancelled: '#6c757d',
      failed: '#dc3545'
    };
    return colors[state] || '#6c757d';
  };

  const getBookingStateLabel = (state) => {
    const labels = {
      received: 'تم الاستلام',
      validating: 'قيد التحقق',
      confirmed: 'مؤكد',
      conflicted: 'يوجد تعارض',
      syncing: 'قيد المزامنة',
      completed: 'مكتمل',
      cancelled: 'ملغي',
      failed: 'فشل'
    };
    return labels[state] || state;
  };

  return (
    <>
      {/* Channex Integration Card (hidden for TurnKey owners) */}
      {!hideInternalDetails && (
      <div className="details-card">
        <h2>
          <img
            src="/channex-logo.png" 
            alt="Channex" 
            style={{ height: '24px', marginLeft: '8px', verticalAlign: 'middle' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          معلومات Channex
        </h2>
        <div className="details-grid">
          <div className="detail-item">
            <label>معرف الحجز في Channex:</label>
            <span className="detail-value">{booking.channexData.bookingId}</span>
          </div>
          
          {booking.channexData.otaReservationCode && (
            <div className="detail-item">
              <label>رقم الحجز في {booking.channexData.otaName || 'OTA'}:</label>
              <span className="detail-value">{booking.channexData.otaReservationCode}</span>
            </div>
          )}

          {booking.channexData.revisionId && (
            <div className="detail-item">
              <label>معرف المراجعة:</label>
              <span className="detail-value small">{booking.channexData.revisionId}</span>
            </div>
          )}

          {booking.channexData.revisionType && (
            <div className="detail-item">
              <label>نوع المراجعة:</label>
              <span className="detail-value">
                {booking.channexData.revisionType === 'new' ? 'حجز جديد' :
                 booking.channexData.revisionType === 'modification' ? 'تعديل' :
                 booking.channexData.revisionType === 'cancellation' ? 'إلغاء' :
                 booking.channexData.revisionType}
              </span>
            </div>
          )}

          {booking.channexData.acknowledgedAt && (
            <div className="detail-item">
              <label>تم التأكيد في:</label>
              <span className="detail-value">{formatDate(booking.channexData.acknowledgedAt)}</span>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Booking State Machine Card (hidden for TurnKey owners) */}
      {!hideInternalDetails && booking.bookingState && (
        <div className="details-card">
          <h2>حالة المعالجة</h2>
          <div className="details-grid">
            <div className="detail-item">
              <label>الحالة الحالية:</label>
              <span 
                className="status-badge"
                style={{ 
                  backgroundColor: getBookingStateColor(booking.bookingState),
                  padding: '4px 12px',
                  borderRadius: '4px',
                  color: 'white',
                  fontSize: '14px'
                }}
              >
                {getBookingStateLabel(booking.bookingState)}
              </span>
            </div>

            {booking.processingMetadata?.processingTimeMs && (
              <div className="detail-item">
                <label>وقت المعالجة:</label>
                <span className="detail-value">
                  {(booking.processingMetadata.processingTimeMs / 1000).toFixed(2)} ثانية
                </span>
              </div>
            )}

            {booking.processingMetadata?.receivedAt && (
              <div className="detail-item">
                <label>تم الاستلام:</label>
                <span className="detail-value">{formatDate(booking.processingMetadata.receivedAt)}</span>
              </div>
            )}

            {booking.processingMetadata?.confirmedAt && (
              <div className="detail-item">
                <label>تم التأكيد:</label>
                <span className="detail-value">{formatDate(booking.processingMetadata.confirmedAt)}</span>
              </div>
            )}
          </div>

          {/* State History Timeline */}
          {booking.stateHistory && booking.stateHistory.length > 0 && (
            <div className="state-history" style={{ marginTop: '20px' }}>
              <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>سجل الحالات</h3>
              <div className="timeline" style={{ paddingLeft: '20px' }}>
                {booking.stateHistory.map((history, index) => (
                  <div key={index} className="timeline-item" style={{ 
                    marginBottom: '10px',
                    paddingLeft: '15px',
                    borderLeft: '2px solid #e0e0e0',
                    position: 'relative'
                  }}>
                    <div style={{
                      position: 'absolute',
                      left: '-5px',
                      top: '5px',
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: getBookingStateColor(history.state)
                    }}></div>
                    <div style={{ fontSize: '14px' }}>
                      <strong>{getBookingStateLabel(history.state)}</strong>
                      {history.reason && <span style={{ marginRight: '8px', color: '#666' }}>- {history.reason}</span>}
                      <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>
                        {formatDate(history.timestamp)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Conflict Information Card */}
      {booking.conflictData?.hasConflict && (
        <div className="details-card" style={{ borderLeft: '4px solid #dc3545' }}>
          <h2 style={{ color: '#dc3545' }}>⚠️ معلومات التعارض</h2>
          <div className="details-grid">
            <div className="detail-item">
              <label>حالة التعارض:</label>
              <span className="detail-value" style={{ color: '#dc3545', fontWeight: 'bold' }}>
                {booking.conflictData.resolutionStatus === 'winner' ? '✅ الحجز الفائز (الأول)' :
                 booking.conflictData.resolutionStatus === 'requires_action' ? '⚠️ يتطلب إجراء فوري' :
                 booking.conflictData.resolutionStatus === 'resolved' ? '✓ تم الحل' :
                 booking.conflictData.resolutionStatus || 'قيد المراجعة'}
              </span>
            </div>

            {booking.conflictData.conflictingBookings && booking.conflictData.conflictingBookings.length > 0 && (
              <div className="detail-item">
                <label>الحجوزات المتعارضة:</label>
                <span className="detail-value">
                  {booking.conflictData.conflictingBookings.length} حجز
                </span>
              </div>
            )}

            {booking.conflictData.resolutionNotes && (
              <div className="detail-item full-width">
                <label>ملاحظات الحل:</label>
                <div className="detail-value" style={{ whiteSpace: 'pre-wrap' }}>
                  {booking.conflictData.resolutionNotes}
                </div>
              </div>
            )}

            {booking.conflictData.resolvedAt && (
              <div className="detail-item">
                <label>تم الحل في:</label>
                <span className="detail-value">{formatDate(booking.conflictData.resolvedAt)}</span>
              </div>
            )}
          </div>

          {booking.conflictData.resolutionStatus === 'requires_action' && (
            <div className="alert alert-danger" style={{ marginTop: '15px' }}>
              <strong>إجراء مطلوب:</strong> يجب التواصل مع الضيف فوراً لحل التعارض في الحجز
            </div>
          )}
        </div>
      )}

      {/* Booking financials deliberately live in the المعلومات المالية card in
          BookingDetails.js, NOT here. This component owns Channex integration data only —
          money belongs to the booking whatever source supplied it (Channex, Gathern iCal,
          manual). Do not reintroduce a financial card here. */}

      {/* Room Details if available */}
      {booking.rooms && booking.rooms.length > 0 && (
        <div className="details-card">
          <h2>تفاصيل الغرف</h2>
          {booking.rooms.map((room, index) => (
            <div key={index} style={{ 
              padding: '10px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '4px',
              marginBottom: '10px'
            }}>
              <div className="details-grid">
                <div className="detail-item">
                  <label>الغرفة {index + 1}:</label>
                  <span className="detail-value">
                    {room.occupancy?.adults || 0} بالغ
                    {room.occupancy?.children > 0 && `, ${room.occupancy.children} طفل`}
                    {room.occupancy?.infants > 0 && `, ${room.occupancy.infants} رضيع`}
                  </span>
                </div>
                
                {room.price && (
                  <div className="detail-item">
                    <label>السعر:</label>
                    <span className="detail-value">{room.price} {booking.currency || 'SAR'}</span>
                  </div>
                )}
                
                {room.nights && (
                  <div className="detail-item">
                    <label>الليالي:</label>
                    <span className="detail-value">{room.nights}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default BookingChannexInfo;